import React, { useEffect, useState } from "react";
import axios from "axios";
import baseURL from "../../../config";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  TextField, IconButton, Typography, Grid, Box, Button, Dialog, DialogTitle,
  DialogContent, DialogActions, Snackbar, Alert, TablePagination
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import Navbar from "../../../Navbar/Navbar";
import { useLocation, useNavigate } from "react-router-dom";

const RankTable = () => {
  const [rankData, setRankData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedRankId, setSelectedRankId] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [error, setError] = useState(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const navigate = useNavigate();

  const fetchRankData = async () => {
    const token = localStorage.getItem("token");
    try {
      const response = await axios.get(`${baseURL}/api/rankData`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRankData(response.data.data);
    } catch (error) {
      console.error("Error fetching rank data:", error);
      setError(error?.response?.data?.message);
    }
  };

  useEffect(() => {
    fetchRankData();
  }, []);

  const location = useLocation();

  useEffect(() => {
    if (location.state?.page !== undefined) {
      setPage(location.state.page);
    }
    if (location.state?.searchTerm) {
      setSearchTerm(location.state.searchTerm);
    }
  }, [location.state]);


  const filteredRanks = rankData.filter((rank) =>
    rank.rankname.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // const handleEdit = (id, rankname) => {
  //   navigate(`/AddRank/${id}`, 
  //     { state: { rankname } });
  // };

  const handleEdit = (id, rankname, type) => {
    navigate(`/AddRank/${id}`, {
      state: {
        rankname,
        type,
        page,
        searchTerm
      }
    });
  };


  const openDeleteDialog = (id) => {
    setSelectedRankId(id);
    setOpenDialog(true);
  };

  const handleDeleteConfirm = async () => {
    const token = localStorage.getItem("token");
    try {
      await axios.delete(`${baseURL}/api/deleteRank/${selectedRankId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOpenDialog(false);
      fetchRankData();
      setSnackbar({ open: true, message: "Rank deleted successfully", severity: "success" });
    } catch (error) {
      console.error("Error deleting rank:", error);
      setSnackbar({ open: true, message: "Failed to delete rank", severity: "error" });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <>
      <Navbar />
      {/* Error message */}
      {error && (
        <Box p={4} textAlign="center" bgcolor="#fdecea" color="#b71c1c">
          <Typography color="error" variant="h6">
            {error}
          </Typography>
        </Box>
      )}
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Grid container alignItems="center" spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={6} sm={4} md={3}>
            <TextField
              label="Search Rank"
              variant="outlined"
              size="small"
              fullWidth
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6} textAlign="center">
            <Typography variant="h5" fontWeight="bold">Rank Table</Typography>
          </Grid>
          <Grid item xs={12} sm={2} md={3} textAlign="right">
            <Button variant="contained" color="primary" size="small" onClick={() => navigate("/AddRank")}>
              Add New Rank
            </Button>
          </Grid>
        </Grid>

        {/* Table */}
        <TableContainer component={Paper} elevation={3}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                <TableCell sx={{ width: '100px' }}><b>SR No.</b></TableCell>
                <TableCell><b>Rank Name</b></TableCell>
                <TableCell><b>Type</b></TableCell>
                <TableCell><b>Required Documents</b></TableCell>
                <TableCell sx={{ width: '200px' }}><b>Action</b></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRanks.length > 0 ? (
                filteredRanks
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((rank, index) => (
                    <TableRow key={rank._id} hover>
                      {/* {console.log("rank", rank)} */}
                      <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                      <TableCell>{rank.rankname}</TableCell>
                      <TableCell>{rank.type}</TableCell>
                      <TableCell>
                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 1,
                          }}
                        >
                          {rank.requireddocuments?.map((doc, index) => (
                            <Box
                              key={index}
                              sx={{
                                backgroundColor: "#f0f0f0",
                                borderRadius: "4px",
                                padding: "2px 6px",
                                fontSize: "0.85rem",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {doc}
                            </Box>
                          ))}
                        </Box>
                      </TableCell>

                      <TableCell>
                        <IconButton color="primary" onClick={() => handleEdit(rank._id, rank?.rankname, rank?.type)}>
                          <EditIcon />
                        </IconButton>
                        <IconButton color="error" onClick={() => openDeleteDialog(rank._id)}>
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
              ) : (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ py: 3, color: "gray" }}>
                    No results found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          {/* Pagination */}
          <TablePagination
            component="div"
            count={filteredRanks.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10]}
          />
        </TableContainer>
      </Box>

      {/* Delete Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>Are you sure you want to delete this rank?</DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} color="inherit">Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default RankTable;
